import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Bot, Power, PowerOff, ShoppingCart, Sparkles, AlertTriangle, CheckCircle2, Info, MessageCircle, Volume2, Mic } from "lucide-react";
import DashboardShell from "@/components/hola/DashboardShell";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";
import { useSettings } from "@/lib/settings";

export default function AuraPage() {
  return (
    <DashboardShell active="aura">
      {() => <AuraBody />}
    </DashboardShell>
  );
}

function AuraBody() {
  const settings = useSettings();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);
  const [buyingPackId, setBuyingPackId] = useState(null);
  const [params, setParams] = useSearchParams();

  const load = async () => {
    try {
      const { data } = await api.get("/merchants/me/aura/report");
      setReport(data);
    } catch (e) {
      toast.error("No pude cargar el reporte de Aura");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Verificar compra tras el redirect de Stripe
  useEffect(() => {
    const purchase = params.get("purchase");
    const sessionId = params.get("session_id");
    if (purchase === "success" && sessionId) {
      (async () => {
        try {
          const { data } = await api.get(`/merchants/me/aura/purchase/verify?session_id=${sessionId}`);
          if (data.paid && data.minutes_added > 0) {
            toast.success(`¡Se acreditaron ${data.minutes_added} minutos de Aura!`);
          } else if (data.already_credited) {
            toast.info("Esta compra ya se había acreditado");
          } else {
            toast.info("Aún estamos confirmando tu pago…");
          }
          await load();
        } catch (e) {
          toast.error("No se pudo verificar la compra");
        } finally {
          params.delete("purchase");
          params.delete("session_id");
          setParams(params);
        }
      })();
    } else if (purchase === "canceled") {
      toast.info("Compra cancelada");
      params.delete("purchase");
      setParams(params);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- solo al montar: procesa el retorno de Stripe una vez

  const toggle = async () => {
    if (!report) return;
    setToggling(true);
    const next = !report.is_active;
    try {
      await api.post("/merchants/me/aura/toggle", { active: next });
      setReport((r) => ({ ...r, is_active: next }));
      toast.success(next ? "Aura activada para tu comercio" : "Aura pausada — no consumirá más minutos");
    } catch {
      toast.error("No pude cambiar el estado");
    } finally {
      setToggling(false);
    }
  };

  const buy = async (pack) => {
    setBuyingPackId(pack.id);
    try {
      const { data } = await api.post("/merchants/me/aura/purchase", {
        pack_id: pack.id,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No pude iniciar la compra");
      setBuyingPackId(null);
    }
  };

  if (loading) return <div className="text-sm text-neutral-500">Cargando…</div>;
  if (!report) return <div className="text-sm text-red-500">No hay datos.</div>;

  const totalMonthly = report.free_minutes_monthly + Math.max(0, report.paid_minutes_remaining);
  const totalRemaining = report.total_minutes_remaining;
  const pctUsed = totalMonthly > 0 ? Math.min(100, Math.round(((totalMonthly - totalRemaining) / totalMonthly) * 100)) : 0;
  const isLow = report.free_minutes_remaining < 10 && report.paid_minutes_remaining <= 0;
  const isExhausted = report.is_exhausted;
  const breakdown = report.breakdown_by_kind || {};

  return (
    <div className="space-y-6 max-w-5xl">
      <div>
        <p className="text-xs uppercase tracking-widest text-neutral-500 font-bold">Aura Guía Virtual</p>
        <h1 className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight">
          Minutos y consumo de Aura
        </h1>
        <p className="text-sm text-neutral-500 mt-2 max-w-2xl leading-relaxed">
          Aquí ves cuántos minutos de Aura le quedan a tu comercio este mes, cómo se calculan, y puedes comprar más o pausar el servicio cuando quieras.
        </p>
      </div>

      {/* Alertas críticas */}
      {isExhausted && (
        <div className="rounded-2xl bg-red-50 border-2 border-red-400 p-5 flex gap-4 shadow-lg animate-pulse" data-testid="aura-exhausted-alert">
          <AlertTriangle className="w-8 h-8 text-red-500 shrink-0" />
          <div className="flex-1">
            <div className="font-heading font-extrabold text-red-800 text-lg">Se acabaron tus minutos de Aura</div>
            <div className="text-sm text-red-700 mt-1">
              Aura dejó de presentar tu comercio a los usuarios que pasen cerca. Compra un pack abajo para reactivarla ahora mismo.
            </div>
          </div>
        </div>
      )}
      {!isExhausted && isLow && (
        <div className="rounded-2xl bg-amber-50 border-2 border-amber-400 p-4 flex gap-3" data-testid="aura-low-alert">
          <AlertTriangle className="w-6 h-6 text-amber-600 shrink-0" />
          <div className="text-sm text-amber-800">
            <strong>Quedan menos de 10 minutos gratis</strong> este mes. Considera comprar un pack para no perder visibilidad cuando se acaben.
          </div>
        </div>
      )}

      {/* Card principal: minutos disponibles */}
      <div className="rounded-2xl bg-gradient-to-br from-[#FF007A] to-[#0055FF] p-6 md:p-8 text-white shadow-xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest font-bold opacity-80">Minutos totales restantes</div>
            <div className="font-heading font-extrabold text-6xl md:text-7xl mt-1" data-testid="aura-total-minutes">
              {totalRemaining.toFixed(1)}
              <span className="text-2xl md:text-3xl opacity-70 ml-2">min</span>
            </div>
            <div className="text-sm opacity-90 mt-2">
              {report.free_minutes_remaining.toFixed(1)} gratis + {report.paid_minutes_remaining.toFixed(1)} comprados
            </div>
          </div>
          <div className="hidden md:block">
            <div className="w-20 h-20 rounded-full bg-white/15 backdrop-blur grid place-items-center">
              <Bot className="w-10 h-10" />
            </div>
          </div>
        </div>

        {/* Barra de uso */}
        <div className="mt-6">
          <div className="flex justify-between text-xs opacity-90 mb-1">
            <span>Uso del ciclo</span>
            <span>{pctUsed}%</span>
          </div>
          <div className="w-full h-3 bg-white/20 rounded-full overflow-hidden">
            <div
              className="h-full bg-white transition-all duration-500"
              style={{ width: `${pctUsed}%` }}
              data-testid="aura-usage-bar"
            />
          </div>
        </div>

        {/* Toggle activo/inactivo */}
        <div className="mt-6 flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between bg-white/10 backdrop-blur rounded-xl p-4">
          <div>
            <div className="font-bold text-sm">Estado de Aura para tu comercio</div>
            <div className="text-xs opacity-80 mt-0.5">
              {report.is_active
                ? "Activa — presenta tu comercio a usuarios cercanos y consume minutos"
                : "Pausada — Aura NO habla de tu comercio (no gastas minutos ni ganas visibilidad)"}
            </div>
          </div>
          <Button
            onClick={toggle}
            disabled={toggling}
            className={`rounded-full font-bold h-11 px-6 border-0 ${
              report.is_active
                ? "bg-white text-red-600 hover:bg-red-50"
                : "bg-white text-green-600 hover:bg-green-50"
            }`}
            data-testid="aura-toggle-btn"
          >
            {report.is_active ? <><PowerOff className="w-4 h-4" /> Inactivar Aura</> : <><Power className="w-4 h-4" /> Activar Aura</>}
          </Button>
        </div>
      </div>

      {/* Packs de compra */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <ShoppingCart className="w-5 h-5 text-[#FF007A]" />
          <h2 className="font-heading font-extrabold text-xl">Comprar más minutos</h2>
        </div>
        <div className="grid sm:grid-cols-3 gap-4">
          {report.packs.map((pack, idx) => (
            <PackCard
              key={pack.id}
              pack={pack}
              highlighted={idx === 1}
              buying={buyingPackId === pack.id}
              onBuy={() => buy(pack)}
            />
          ))}
        </div>
        <div className="mt-3 flex gap-2 text-xs text-neutral-500 items-start">
          <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          <div>Los minutos comprados <strong>no expiran</strong> y se suman a tus 50 minutos gratis mensuales.</div>
        </div>
      </div>

      {/* Consumo detallado + reglas */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-neutral-200 bg-white p-5">
          <h3 className="font-heading font-extrabold text-base mb-3">Consumo este ciclo</h3>
          <ConsumoRow icon={MessageCircle} label="Chat de texto" turns={breakdown.chat?.turns || 0} costMxn={breakdown.chat?.cost_mxn || 0} minutesEach={20/60} />
          {settings.aura_voice_enabled && (<><ConsumoRow icon={Volume2} label="Voz (Aura habla)" turns={breakdown.voice_tts?.turns || 0} costMxn={breakdown.voice_tts?.cost_mxn || 0} />
          <ConsumoRow icon={Mic} label="Voz (usuario habla)" turns={breakdown.voice_stt?.turns || 0} costMxn={breakdown.voice_stt?.cost_mxn || 0} last /></>)}
        </div>

        <div className="rounded-2xl border border-neutral-200 bg-white p-5">
          <h3 className="font-heading font-extrabold text-base mb-3">¿Cómo se cobra?</h3>
          <ul className="space-y-2 text-sm text-neutral-700">
            <li className="flex gap-2">
              <CheckCircle2 className="w-4 h-4 text-[#FF007A] shrink-0 mt-0.5" />
              <span><strong>Sólo pagas cuando el usuario marcó interés</strong> en tu comercio y Aura sigue hablando de ti.</span>
            </li>
            <li className="flex gap-2">
              <CheckCircle2 className="w-4 h-4 text-[#FF007A] shrink-0 mt-0.5" />
              <span>Cada <strong>respuesta de Aura por chat = 20 segundos</strong> (equivalente al tiempo que toma leerla).</span>
            </li>
            <li className="flex gap-2">
              <CheckCircle2 className="w-4 h-4 text-[#FF007A] shrink-0 mt-0.5" />
              <span>{settings.aura_voice_enabled ? <>La <strong>voz</strong> descuenta la duración real del audio (que Aura habla + que el usuario contesta).</> : <>Aura atiende por <strong>chat</strong> (texto): cada respuesta descuenta segundos según su longitud.</>}</span>
            </li>
            <li className="flex gap-2">
              <CheckCircle2 className="w-4 h-4 text-[#FF007A] shrink-0 mt-0.5" />
              <span>Cuando el usuario pasea sin haberse fijado en ti aún, <strong>NO se te cobra nada</strong>.</span>
            </li>
            <li className="flex gap-2">
              <Sparkles className="w-4 h-4 text-[#0055FF] shrink-0 mt-0.5" />
              <span>Al renovar tu mensualidad recibes <strong>50 minutos gratis nuevos</strong> automáticamente.</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}

function PackCard({ pack, highlighted, buying, onBuy }) {
  const pricePerMin = (pack.price_mxn / pack.minutes).toFixed(2);
  return (
    <div
      className={`rounded-2xl p-5 border-2 flex flex-col ${
        highlighted
          ? "border-[#FF007A] bg-gradient-to-br from-[#FF007A]/5 to-[#0055FF]/5 shadow-lg relative"
          : "border-neutral-200 bg-white"
      }`}
      data-testid={`aura-pack-${pack.id}`}
    >
      {highlighted && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#FF007A] text-white text-[10px] font-extrabold uppercase tracking-widest px-3 py-1 rounded-full">
          Más popular
        </div>
      )}
      <div className="text-xs uppercase tracking-widest text-neutral-500 font-bold">{pack.label}</div>
      <div className="mt-2 font-heading font-extrabold text-4xl leading-none">
        {pack.minutes}<span className="text-lg opacity-60 ml-1">min</span>
      </div>
      <div className="mt-3 flex items-baseline gap-1">
        <span className="text-3xl font-heading font-extrabold text-[#0055FF]">${pack.price_mxn}</span>
        <span className="text-sm text-neutral-500">MXN</span>
      </div>
      <div className="text-xs text-neutral-500 mt-1">≈ ${pricePerMin} por minuto</div>

      <Button
        onClick={onBuy}
        disabled={buying}
        className={`mt-4 rounded-full font-bold h-11 ${
          highlighted
            ? "bg-[#FF007A] hover:bg-[#e60070]"
            : "bg-[#0055FF] hover:bg-[#0044cc]"
        }`}
        data-testid={`aura-buy-${pack.id}`}
      >
        {buying ? "Redirigiendo…" : <><ShoppingCart className="w-4 h-4" /> Comprar</>}
      </Button>
    </div>
  );
}

function ConsumoRow({ icon: Icon, label, turns, costMxn, last }) {
  return (
    <div className={`flex items-center justify-between py-3 ${last ? "" : "border-b border-neutral-100"}`}>
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-full bg-neutral-100 grid place-items-center">
          <Icon className="w-4 h-4 text-neutral-700" />
        </div>
        <div>
          <div className="text-sm font-bold">{label}</div>
          <div className="text-xs text-neutral-500">{turns} {turns === 1 ? "turno" : "turnos"}</div>
        </div>
      </div>
      <div className="text-right">
        <div className="text-sm font-heading font-extrabold text-[#0055FF]">${costMxn.toFixed(2)} <span className="text-xs opacity-60">MXN</span></div>
      </div>
    </div>
  );
}
